import { trimLeadChar } from "@utils/trimLeadChar";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { Link } from "react-router";

import { LoadingIndicator } from "../../ui-components/LoadingIndicator/LoadingIndicator";
import { ActionsPopover, ActionsPopoverAction } from "../ActionsPopover/ActionsPopover";
import { Icon } from "../Icon/Icon";
import { Label } from "../Label/Label";
import { Text } from "../Text/Text";
import styles from "./DataTable.module.css";
import { DataTableCell } from "./DataTableCell";

/** A header element (column) */
export interface DataTableHeader {
  /** An unique key for the DataTableHeader */
  key: string;
  /** The display title for the DataTableHeader */
  title: string;
  /** An optional suffix that should be appended to all DataTableDatas in this column */
  suffix?: string;
  /** An optional width for this column */
  width?: string;
  /** An optional icon name displayed besides the table column name */
  icon?: string;
  /** Optional ActionsPopoverActions */
  popoverActions?: ActionsPopoverAction[];
  /** Optional other component */
  customComponent?: JSX.Element;
  align?: "flex-start" | "flex-end";
}

export type DataTableRowHeight = "sm" | "md" | "lg";
export type DataTableSortDirection = "ascending" | "descending";

export interface DataTableDataJSXElement {
  /** The custom JSX element or elements displayed */
  element: JSX.Element[] | JSX.Element;
  /** An optional raw string used when exporting the DataTable */
  rawString?: string;
  /** An optional raw data used when exporting the DataTable */
  rawData?: any;
}

export interface DataTableRawStringElement {
  displayString: string;
  rawData: any;
}

export interface DataTableV2Element {
  cellValue: () => DataTableDataCellValue;
  loader?: () => Promise<void>;
}
export const isDataTableV2Element = (element: DataTableDataCellValue): element is DataTableV2Element =>
  !!element && typeof (element as DataTableV2Element)?.cellValue === "function";

/** The supported values for a DataTable */
export type DataTableDataCellValue = number | string | Date | DataTableDataJSXElement | DataTableRawStringElement | DataTableV2Element | undefined;

/** Data for a DataTable row */
export interface DataTableData {
  /** Required unique key for the data */
  key: string;
  /** Map of DataTableDataCellValue where the key of each value is equivalent to a DataTableHeader key  */
  cells: {
    [key: string]: DataTableDataCellValue | Promise<DataTableDataCellValue>;
  };
}

export interface DataTableProps {
  /** Array of the DataTableHeader */
  header: DataTableHeader[];
  /** The array of datas for the DataTable*/
  datas: DataTableData[];

  /** The default key to sort the datas based on */
  defaultSortKey?: string;
  /** The default direction to sort */
  defaultSortDirection?: DataTableSortDirection;

  /** The max amount of rows in the table */
  paginationSize?: number;

  /** Supported background colors for the component */
  headerColor?: "white" | "grey";
  /** When true, the DataTable will only display an activity indicator */
  loading?: boolean;
  /** Optional JSX Element that replaces the standard JSX Element of an empty table */
  emptyElement?: string | JSX.Element;
  /** Click handler, where the key is equivalent to the key of a DataTableData  */
  onClick?: (key: string) => void;
  /** Redirect to link on the click of a row. Key is added to end of link */
  linkTo?: string;
  /** Optional function to add redirection */
  getLinkTo?: (key: string) => string | undefined;
  /** Optional classname for the component */
  className?: string;
  /** Optional rowheight. Large is default */
  rowHeight?: DataTableRowHeight;
  /** Optional shadow toggle */
  shadowDisabled?: boolean;
  /** Optional array to add custom order to the table */
  customDataOrder?: string[];
  /** Optional function to add drag and drop sorting */
  didReorderData?: (order: string[]) => void;

  dataCy?: string;
}

/**
 * @deprecated use Table2 instead
 */
export const DataTable = (props: DataTableProps) => {
  const [sortKey, setSortKey] = useState(props.defaultSortKey);
  const [sortDirection, setSortDirection] = useState(props.defaultSortDirection ?? "ascending");
  const [paginationOffset, setPaginationOffset] = useState(0);

  useEffect(() => {
    setSortKey(props.defaultSortKey);
  }, [props.defaultSortKey]);

  useEffect(() => {
    setSortDirection(props.defaultSortDirection ?? "ascending");
  }, [props.defaultSortDirection]);

  const paginationEnd = Math.min(props.datas.length, paginationOffset + (props.paginationSize ?? 0));

  // Reset pagination if the pagination size changes and the current offset is out of bounds
  useEffect(() => {
    if (paginationOffset > props.datas.length) {
      setPaginationOffset(0);
    }
  }, [props.datas.length, paginationOffset, props.paginationSize]);

  function getContent() {
    let datas = [...props.datas];

    if (props.customDataOrder) {
      const customDataOrder = props.customDataOrder;
      datas.sort((a, b) => customDataOrder.indexOf(a.key) - customDataOrder.indexOf(b.key));
    } else if (sortKey) {
      datas.sort((a, b) => {
        let sortA = a.cells[sortKey];
        let sortB = b.cells[sortKey];

        if (typeof sortA === "object" && "rawData" in sortA && sortA.rawData) {
          sortA = sortA.rawData;
        } else if (typeof sortA === "object" && "rawString" in sortA && sortA.rawString) {
          sortA = sortA.rawString;
        }

        if (typeof sortB === "object" && "rawData" in sortB && sortB.rawData) {
          sortB = sortB.rawData;
        } else if (typeof sortB === "object" && "rawString" in sortB && sortB.rawString) {
          sortB = sortB.rawString;
        }

        // in case sorting values are strings then clean them from extra symbols like emoji before comparing
        if (typeof sortA === "string" && typeof sortB === "string") {
          const cleanA = trimLeadChar(sortA);
          const cleanB = trimLeadChar(sortB);

          return sortDirection === "ascending"
            ? cleanA.localeCompare(cleanB, "en", { numeric: true })
            : cleanB.localeCompare(cleanA, "en", { numeric: true });
        }

        return ((sortA ?? "") < (sortB ?? "") ? -1 : 1) * (sortDirection === "ascending" ? 1 : -1);
      });
    }

    if (props.paginationSize) {
      datas = datas.slice(paginationOffset, paginationEnd);
    }

    if (props.loading) {
      return (
        <tr>
          <td colSpan={props.header.length}>
            <LoadingIndicator className={styles.statusElement} />
          </td>
        </tr>
      );
    } else if (datas.length > 0) {
      return datas.map((data: DataTableData, index) => {
        const link = props.linkTo ? props.linkTo + data.key : props.getLinkTo?.(data.key);
        if (link) {
          return (
            <Link style={{ display: "contents", color: "var(--primary-text-color)", verticalAlign: "middle" }} to={link} key={`link-${data.key}`}>
              <tr key={data.key} className={styles.selectable} data-cy={props.dataCy && `${props.dataCy}-row`}>
                {props.header.map((header) => {
                  return <DataTableCell key={header.key} data={data} header={header} dataCy={props.dataCy} />;
                })}
              </tr>
            </Link>
          );
        } else if (props.didReorderData) {
          return (
            <Draggable key={data.key} draggableId={data.key} index={index}>
              {(provided) => (
                <tr key={data.key} {...provided?.draggableProps} ref={provided?.innerRef} data-cy={props.dataCy && `${props.dataCy}-row`}>
                  <td className={styles.dragicon} {...provided.dragHandleProps}>
                    <i className="material-icons">drag_indicator</i>
                  </td>
                  {props.header.map((header) => {
                    return <DataTableCell key={header.key} data={data} header={header} draggable />;
                  })}
                </tr>
              )}
            </Draggable>
          );
        }
        return (
          <tr
            key={data.key}
            className={props.onClick !== undefined ? styles.selectable : ""}
            onClick={() => {
              if (props.onClick !== undefined) props.onClick(data.key);
            }}
            data-cy={props.dataCy && `${props.dataCy}-row`}
          >
            {props.header.map((header) => {
              return <DataTableCell key={header.key} data={data} header={header} />;
            })}
          </tr>
        );
      });
    } else if (!props.loading) {
      if (typeof props.emptyElement === "string") {
        return (
          <tr>
            <td colSpan={props.header.length}>
              <Label type="paragraph" text={props.emptyElement} className={styles.statusElement} />
            </td>
          </tr>
        );
      } else if (props.emptyElement) {
        return (
          <tr>
            <td colSpan={props.header.length}>{props.emptyElement}</td>
          </tr>
        );
      } else {
        return (
          <tr>
            <td colSpan={props.header.length}>
              <div className={styles.statusElement} style={{ lineHeight: "2em" }}>
                <Label type="subtitle" text="🤷‍♂️" />
                <Label data-cy="empty-table-default-content" type="paragraph" text={t("main.noDataToDisplay")} />
              </div>
            </td>
          </tr>
        );
      }
    }
  }

  function didClickHeader(key: string) {
    if (sortKey !== key) {
      setSortKey(key);
    } else {
      setSortDirection(sortDirection === "ascending" ? "descending" : "ascending");
    }
  }

  function paginateBackwards() {
    const newStart = Math.max(0, paginationOffset - (props.paginationSize ?? 0));
    setPaginationOffset(newStart);
  }
  function paginateForwards() {
    const newStart = Math.min(props.datas.length, paginationOffset + (props.paginationSize ?? 0));
    setPaginationOffset(newStart);
  }

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const newTaskOrder = props.customDataOrder ? [...props.customDataOrder] : [...props.datas.map((data) => data.key)];
    const [removed] = newTaskOrder.splice(source.index, 1);
    newTaskOrder.splice(destination.index, 0, removed);

    props.didReorderData?.(newTaskOrder);
  };

  return (
    <div className={styles.wrapper}>
      <table
        className={
          styles.table +
          " " +
          props.className +
          (props.headerColor === "grey" ? " " + styles.border : "") +
          (props.shadowDisabled ? " " + styles.shadowDisabled : "") +
          (props.rowHeight === "sm" ? " " + styles.sm : "") +
          (props.rowHeight === "md" ? " " + styles.md : "")
        }
        data-cy={props.dataCy}
      >
        <thead>
          <tr>
            {props.didReorderData && <Text size="md"></Text>}
            {props.header.map((header) => {
              const headerTSX = (
                <Text
                  size="md"
                  isBold
                  leadingIcon={header.icon}
                  onClick={() => {
                    if (props.customDataOrder) {
                      return;
                      // skip sorting for actions column
                    } else if (header.key !== "actions") {
                      return didClickHeader(header.key);
                    }
                  }}
                  trailingIcon={header.key === sortKey ? { name: sortDirection === "ascending" ? "arrow_drop_up" : "arrow_drop_down" } : undefined}
                  style={{ justifyContent: header.align || "flex-start" }}
                >
                  {header.title}
                </Text>
              );
              return header.customComponent ? (
                <th
                  key={header.key}
                  style={{ backgroundColor: props.headerColor === "grey" ? "#fafafa" : "", width: header.width, cursor: "pointer" }}
                >
                  {header.customComponent}
                </th>
              ) : header.popoverActions ? (
                <th
                  key={header.key}
                  style={{ backgroundColor: props.headerColor === "grey" ? "#fafafa" : "", width: header.width, cursor: "pointer" }}
                >
                  <ActionsPopover position="bottom left" trigger={headerTSX} actions={header.popoverActions} />
                </th>
              ) : (
                <th
                  key={header.key}
                  style={{
                    backgroundColor: props.headerColor === "grey" ? "#fafafa" : "",
                    width: header.width,
                    cursor: "pointer",
                  }}
                >
                  {headerTSX}
                </th>
              );
            })}
          </tr>
        </thead>
        {props.didReorderData ? (
          <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
            <Droppable droppableId="table">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {getContent()}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <tbody>{getContent()}</tbody>
        )}
        {props.paginationSize && (
          <tfoot className={styles.footer}>
            <tr>
              <td colSpan={props.header.length}>
                <div className={styles.pagination}>
                  <Text size="md" marginRight={8}>
                    {`${t("main.showing")} ${paginationOffset + 1}-${paginationEnd} ${t("main.of").toLowerCase()} ${props.datas.length}`}
                  </Text>
                  {paginationOffset > 0 && <Icon name="chevron_left" onClick={() => paginateBackwards()} />}
                  {paginationEnd < props.datas.length && <Icon name="chevron_right" onClick={() => paginateForwards()} />}
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};
